/** How a notification looks: a solid glyph in a round badge, the tab it files under, and its time label. Shared by the bell, the toasts and Account → Notifications; the Invest and More menus and the Earn pages draw their items with the same glyphs. */
import type { ReactNode } from 'react';
import type { AppNotification } from '@platform/shared';
import { cx } from './ui';

export type GlyphName =
  | 'wallet'
  | 'chart'
  | 'risk'
  | 'check'
  | 'shield'
  | 'chat'
  | 'bell'
  // The rest are for the Invest and More menus.
  | 'copy'
  | 'star'
  | 'pie'
  | 'zap'
  | 'gift'
  | 'invite'
  | 'target'
  | 'trophy'
  | 'bag'
  | 'shieldCheck'
  | 'cap'
  | 'book'
  | 'user'
  | 'candles'
  // The Wallet page's balance split, and Earn.
  | 'lock'
  | 'clock'
  | 'flame'
  | 'percent'
  | 'moon'
  | 'info';

const GLYPHS: Record<GlyphName, ReactNode> = {
  wallet: (
    <path
      fillRule="evenodd"
      d="M3 7.5A3.5 3.5 0 0 1 6.5 4h10A1.5 1.5 0 0 1 18 5.5V7h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6.5A3.5 3.5 0 0 1 3 16.5zM6.5 6a1.5 1.5 0 0 0 0 3H16V6zM16.5 12.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5z"
    />
  ),
  chart: (
    <>
      <rect x="3.5" y="13" width="4.5" height="7.5" rx="1.4" />
      <rect x="9.75" y="8.5" width="4.5" height="12" rx="1.4" />
      <rect x="16" y="3.5" width="4.5" height="17" rx="1.4" />
    </>
  ),
  risk: <path fillRule="evenodd" d="M10.27 3.5a2 2 0 0 1 3.46 0l8.06 14a2 2 0 0 1-1.73 3H3.94a2 2 0 0 1-1.73-3zM11 9h2v5h-2zm0 7h2v2h-2z" />,
  check: <path fillRule="evenodd" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.3 13.9-4-4 1.4-1.4 2.6 2.6 5.2-5.2 1.4 1.4z" />,
  shield: (
    <path
      fillRule="evenodd"
      d="M11.3 2.2a2 2 0 0 1 1.4 0l6.5 2.4A1.2 1.2 0 0 1 20 5.7V11c0 5.2-3.5 9.4-8 10.8C7.5 20.4 4 16.2 4 11V5.7a1.2 1.2 0 0 1 .8-1.1zM11 7h2v6h-2zm0 8h2v2h-2z"
    />
  ),
  chat: <path fillRule="evenodd" d="M5 3h14a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-7.5L7 21.6V18H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3zM7 9.5h2.2v2.2H7zm3.9 0h2.2v2.2h-2.2zm3.9 0H17v2.2h-2.2z" />,
  bell: <path d="M12 2.5a6.5 6.5 0 0 0-6.5 6.5c0 3.6-.9 5.8-1.8 7.1-.5.7 0 1.4.8 1.4h15c.8 0 1.3-.7.8-1.4-.9-1.3-1.8-3.5-1.8-7.1A6.5 6.5 0 0 0 12 2.5zM9.2 19a2.9 2.9 0 0 0 5.6 0z" />,
  copy: <path fillRule="evenodd" d="M8 7V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm2 0h5a2 2 0 0 1 2 2v5h2V5h-9z" />,
  star: <path d="M12 2.5l2.9 6 6.6.8-4.9 4.5 1.3 6.5L12 17.1l-5.9 3.2 1.3-6.5-4.9-4.5 6.6-.8z" />,
  pie: (
    <>
      <path d="M11 3.05A9 9 0 1 0 20.95 13H11z" />
      <path d="M13 2.05V11h8.95A9 9 0 0 0 13 2.05z" />
    </>
  ),
  zap: <path d="M13.5 2 4 14h6.5l-1 8L19 10h-6.5z" />,
  gift: (
    <>
      <path d="M3 9a1 1 0 0 1 1-1h7v4H3zM13 8h7a1 1 0 0 1 1 1v3h-8zM4 13.5h7V21H5a1 1 0 0 1-1-1zM13 13.5h7V20a1 1 0 0 1-1 1h-6z" />
      <path d="M12 7.6C11 4.5 7.6 3.6 6.8 5.3 6.1 6.7 8.5 7.8 12 7.8zm0 0c1-3.1 4.4-4 5.2-2.3.7 1.4-1.7 2.5-5.2 2.5z" />
    </>
  ),
  invite: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M1.5 20c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1zM18 7h2v2.5h2.5v2H20V14h-2v-2.5h-2.5v-2H18z" />
    </>
  ),
  target: <path fillRule="evenodd" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12zm0 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />,
  trophy: (
    <path
      fillRule="evenodd"
      d="M6 3h12v1.5h3V8a4 4 0 0 1-3.6 4A6 6 0 0 1 13 15.9V18h3v3H8v-3h3v-2.1A6 6 0 0 1 6.6 12 4 4 0 0 1 3 8V4.5h3zm0 3.5H5V8a2 2 0 0 0 1 1.7zm12 0v3.2A2 2 0 0 0 19 8V6.5z"
    />
  ),
  bag: <path fillRule="evenodd" d="M8 7V6a4 4 0 0 1 8 0v1h3a1 1 0 0 1 1 1.1l-1.2 12a1 1 0 0 1-1 .9H6.2a1 1 0 0 1-1-.9L4 8.1A1 1 0 0 1 5 7zm2 0h4V6a2 2 0 0 0-4 0z" />,
  shieldCheck: (
    <path
      fillRule="evenodd"
      d="M11.3 2.2a2 2 0 0 1 1.4 0l6.5 2.4A1.2 1.2 0 0 1 20 5.7V11c0 5.2-3.5 9.4-8 10.8C7.5 20.4 4 16.2 4 11V5.7a1.2 1.2 0 0 1 .8-1.1zm-.6 13.2 5.2-5.2-1.4-1.4-3.8 3.8-1.8-1.8-1.4 1.4z"
    />
  ),
  cap: (
    <>
      <path d="M12 3 1 8l11 5 9-4.1V15h2V8z" />
      <path d="M6 12.6V16c0 1.9 2.7 3.5 6 3.5s6-1.6 6-3.5v-3.4l-6 2.7z" />
    </>
  ),
  book: <path fillRule="evenodd" d="M5 3h12a3 3 0 0 1 3 3v15H7a3 3 0 0 1-3-3V4a1 1 0 0 1 1-1zm3 5v2h8V8zm0 4v2h6v-2z" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4.5" />
      <path d="M3.5 20.5c0-4.4 3.8-7 8.5-7s8.5 2.6 8.5 7a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z" />
    </>
  ),
  lock: <path fillRule="evenodd" d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zm2 0h6V8a3 3 0 0 0-6 0zm2 4v4h2v-4z" />,
  candles: <path d="M6 2h2v3h1.5a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8v3H6v-3H4.5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1H6zM16 4h2v4h1.5a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H18v5h-2v-5h-1.5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1H16z" />,
  clock: <path fillRule="evenodd" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 5h2v5.4l3.6 2.1-1 1.7L11 13.6z" />,
  flame: <path d="M12.5 2c.6 3.2 4.5 5.4 4.5 10.2A5 5 0 0 1 7 12.5c0-2.2 1.1-3.8 2.3-4.8 0 2 .8 3.3 2 3.6-.3-3.4.1-6.6 1.2-9.3z" />,
  percent: <path d="M18.4 4.2l1.4 1.4L5.6 19.8l-1.4-1.4zM7 4a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm10 10a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />,
  moon: <path d="M14.5 2.5A9.5 9.5 0 1 0 21.5 15 7.5 7.5 0 0 1 14.5 2.5z" />,
  info: <path fillRule="evenodd" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 9h2v6h-2zm0-4h2v2h-2z" />,
};

export type NotificationTab = 'account' | 'trading' | 'other';

/** The glyph, filter tab and urgency of a notification, from its push category. */
export function notificationMeta(n: Pick<AppNotification, 'category' | 'kind'>): { glyph: GlyphName; tab: NotificationTab; urgent: boolean } {
  const c = n.category ?? 'account.message';
  if (c.startsWith('risk.')) return { glyph: 'risk', tab: 'trading', urgent: true };
  if (c === 'market.event_risk') return { glyph: 'clock', tab: 'trading', urgent: true };
  if (c === 'market.alert' || c === 'market.big_move' || c.endsWith('_summary')) return { glyph: 'chart', tab: 'trading', urgent: false };
  if (c.startsWith('trade.') || c.startsWith('market.')) return { glyph: 'check', tab: 'trading', urgent: false };
  if (c === 'account.funding' || c === 'account.rewards') return { glyph: 'wallet', tab: 'account', urgent: false };
  if (c === 'account.verification' || c.startsWith('security.')) return { glyph: 'shield', tab: 'account', urgent: false };
  if (c === 'account.message') return { glyph: 'chat', tab: 'account', urgent: false };
  return { glyph: 'bell', tab: 'other', urgent: false };
}

export function Glyph({ name, size = 20 }: { name: GlyphName; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {GLYPHS[name]}
    </svg>
  );
}

/** Round badge; an urgent one (margin, stop-out) is inverted so it stands out without colour. With `color`, the glyph, ring and a light tint take that colour instead (the Wallet's balance split). */
export function GlyphBadge({ name, size = 'md', urgent = false, color }: { name: GlyphName; size?: 'xs' | 'sm' | 'md' | 'lg'; urgent?: boolean; color?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'grid shrink-0 place-items-center rounded-full',
        size === 'lg' ? 'size-16' : size === 'md' ? 'size-12' : size === 'sm' ? 'size-10' : 'size-[34px]',
        !color && 'ring-1 ring-inset',
        !color && (urgent ? 'bg-fg text-bg ring-fg' : 'bg-track text-fg ring-line-strong'),
      )}
      style={
        color
          ? { color, background: `color-mix(in srgb, ${color} 16%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 40%, transparent)` }
          : undefined
      }
    >
      <Glyph name={name} size={size === 'lg' ? 28 : size === 'md' ? 20 : size === 'sm' ? 18 : 16} />
    </span>
  );
}

export function NotificationIcon({ n, size = 'md' }: { n: Pick<AppNotification, 'category' | 'kind'>; size?: 'sm' | 'md' }) {
  const m = notificationMeta(n);
  return <GlyphBadge name={m.glyph} size={size} urgent={m.urgent} />;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** "Today" or "Earlier", for the list headings. */
export const dayGroup = (iso: string) => (new Date(iso).getTime() >= startOfToday() ? 'Today' : 'Earlier');

/** "now", "12m", "09:42" today, "17 Oct" before that. */
export function timeLabel(iso: string) {
  const t = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (t >= startOfToday()) return new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** The action a notification's link leads to, named from where it goes. */
export function ctaLabel(n: Pick<AppNotification, 'category' | 'link'>) {
  const link = n.link ?? '';
  if (n.category?.startsWith('risk.')) return 'Review position';
  if (link.startsWith('/trade')) return 'Open chart';
  if (link.startsWith('/account/deposit')) return 'View deposit';
  if (link.startsWith('/account/withdraw')) return 'View withdrawal';
  if (link.startsWith('/account/kyc')) return 'Upload document';
  if (link.startsWith('/account/source-of-funds')) return 'Send statements';
  if (link.startsWith('/account/support')) return 'Open Support';
  if (link.startsWith('/onboarding')) return 'Continue';
  if (link.startsWith('/portfolios/') || link.startsWith('/portfolio/smart/')) return 'View portfolio';
  if (link.startsWith('/portfolio')) return 'Open portfolio';
  if (link.startsWith('/earn')) return 'View rewards';
  if (link.startsWith('/wallet')) return 'Open wallet';
  return 'View';
}
